import { useMemo } from 'react';
import { Controller, useForm, useFormState } from 'react-hook-form';
import get from 'lodash/get';

import { FormPublishingFooter } from '@components';
import { useRelatedMinMaxValidation } from '@hooks';
import {
  DatamodelFormField,
  FieldWrapper,
  FormError,
  FormFieldset,
  LocationsField,
  PathField,
  TextAreaField,
  TextField,
  ToggleField,
  DatamodelSubTypeField,
} from '@Forms/fields.jsx';
import {
  removeNewlines,
  pathFormat,
  slugify,
  slugifyDash,
  slugifyUnderscore,
  urlFormat,
  addSlash,
  slugifyOnBlur,
} from '@Forms/formatters.js';
import { errorsFromMutate } from '@Forms/utils.js';
import { forceTrailingSlash } from '@helpers';

// default value for a custom field slug missing from pageData.custom_fields
// (e.g. a page that's never been through a full save/publish cycle). without
// an explicit key here, react-hook-form's values-driven reset can't tell "no
// value" apart from "not part of this reset" and leaves the DOM input as-is,
// which is what made a draft revert look like it kept the edited value.
const ARRAY_CUSTOM_FIELD_TYPES = new Set([
  'related_model_list',
  'item_list',
  'datamodel_sub_type',
]);

const defaultForFieldType = (fieldType) => {
  if (fieldType === 'boolean') {
    return false;
  }
  if (ARRAY_CUSTOM_FIELD_TYPES.has(fieldType)) {
    return [];
  }
  return '';
};

export const PageForm = ({
  datamodelData,
  disabled,
  mutation,
  pageData,
  pageTemplate,
  publishMutation,
  relatedDatamodels,
  siteData,
}) => {
  const normalizedPageData = useMemo(() => {
    if (!pageData) {
      return {};
    }
    const customFieldsDefaults = {};
    (datamodelData?.fields || []).forEach((field) => {
      customFieldsDefaults[field.slug] = defaultForFieldType(field.field_type);
    });
    return {
      ...pageData,
      custom_fields: {
        ...customFieldsDefaults,
        ...(pageData.custom_fields || {}),
      },
    };
  }, [pageData, datamodelData]);

  const useFormReturn = useForm({
    values: normalizedPageData,
    mode: 'onBlur',
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
    setError,
    clearErrors,
    setFocus,
    watch,
  } = useFormReturn;
  const { isDirty, isValid } = useFormState({ control });

  const onMutationError = (error) => {
    // shared mutation callback
    //
    // handle server-side validations and errors
    const errorsList = errorsFromMutate(error);
    errorsList
      .filter(({ field }) => field !== '__all__')
      .forEach(({ field, error }) => setError(field, error));
  };

  const onRevert = () => {
    const data = {
      is_draft: false,
      published_draft_id: null,
    };
    // rely on the setQueryData in onPageSaveSuccess (SitePage.jsx) to refresh
    // pageData; the "values" option on useForm() then resyncs the form
    publishMutation.mutate(data, {
      onError: (error) => onMutationError(error),
    });
  };

  const onSubmit = async (data, e) => {
    // tracking of which button is clicked
    const submitter = e?.nativeEvent?.submitter;
    // need to force a slash in case path field was not blurred
    data.path = forceTrailingSlash(data.path);
    // take draft info from latest GET request
    data.published_draft = pageData.published_draft;
    data.published_draft_id = pageData.published_draft_id;

    console.log('onSubmit', data, submitter.name);

    if (submitter.name == 'publish') {
      // publish button
      data.is_draft = false;
      publishMutation.mutate(data, {
        onError: (error) => onMutationError(error),
      });
    } else if (submitter.name == 'draft') {
      // draft save (default submit)
      data.is_draft = true;
      // do not update certain fields in draft as they are not supported and
      // can cause problems with sitemaps, etc.
      //
      // need a backend update to fix this.
      delete data.path;
      delete data.name;
      delete data.noindex;
      delete data.canonical;
      delete data.custom_field_published;
      delete data.custom_field_disable_canonical;
      reset({
        ...data,
        canonical: pageData.canonical,
        custom_field_disable_canonical: pageData.custom_field_disable_canonical,
        custom_field_published: pageData.custom_field_published,
        name: pageData.name,
        noindex: pageData.noindex,
        path: pageData.path,
      });
      mutation.mutate(data, {
        onError: (error) => onMutationError(error),
      });
    }
  };

  // combined isPending and isError
  const isError = mutation.isError || publishMutation.isError;
  const isPending = mutation.isPending;

  const relatedModelListFields = useMemo(() => {
    return (datamodelData?.fields || [])
      .filter((f) => f.field_type === 'related_model_list')
      .map((f) => ({
        slug: f.slug,
        required: !!f.required,
        min_items: f.min_items,
        max_items: f.max_items,
      }));
  }, [datamodelData?.fields]);

  useRelatedMinMaxValidation({
    fields: relatedModelListFields,
    namePrefix: 'custom_fields',
    watch,
    errors,
    setError,
    clearErrors,
  });

  // list of dynamic fields
  // prefix added to target custom_fields.
  const fieldsList = datamodelData.fields.map((field) => {
    const fieldName = `custom_fields.${field.slug}`;
    const fieldError = get(errors, fieldName);
    return (
      <div key={field.id}>
        <DatamodelFormField
          datamodelField={field}
          fieldPrefix={`custom_fields.`}
          relatedDatamodels={relatedDatamodels}
          siteID={siteData.id}
          useFormReturn={useFormReturn}
        />
        {field.field_type === 'related_model_list' && fieldError?.message && (
          <FormError message={fieldError.message} />
        )}
      </div>
    );
  });

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={
        `sb-manage-content-form ` + (disabled ? 'sb-form-disabled' : '')
      }
    >
      <FormFieldset>
        <TextField
          register={register}
          error={errors.name}
          options={{ required: 'This field is required' }}
          name={`name`}
          label="Page name"
          helpText="Name of the page used within navigations and menus"
          placeholder="My page"
          autoComplete="off"
        />
        <h2>{pageTemplate.name} fields</h2>
        {fieldsList}
      </FormFieldset>
      <FormFieldset legend="Publishing options">
        <ToggleField
          register={register}
          control={control}
          error={errors.active}
          name="active"
          label="Published"
          helpText="When unpublished, will 404 if visited."
        />
        <TextField
          register={register}
          error={errors.custom_field_published}
          name={`custom_field_published`}
          label="Published status from custom field"
          options={{
            onChange: slugifyUnderscore,
            onBlur: (e) => slugifyOnBlur(e, '_'),
          }}
          helpText="Key reference to the Site custom field that overrides the Page published status (i.e. some_field)"
        />
        {siteData.business_id && (
          <FieldWrapper
            error={errors.locations}
            label="Assign locations"
            name={'locations'}
          >
            <Controller
              control={control}
              name={'locations'}
              render={({ field, value, name, ref }) => (
                <LocationsField
                  businessID={siteData.business_id}
                  field={field}
                  defaultLocations={pageData.locations || []}
                />
              )}
            />
          </FieldWrapper>
        )}
        <FieldWrapper
          error={get(errors, 'tags')}
          label="Page tag"
          name="tags"
          helpText="Special content database item type."
        >
          <Controller
            control={control}
            name="tags"
            render={({ field }) => (
              <DatamodelSubTypeField
                field={field}
                defaultOptions={pageData.tags}
              />
            )}
          />
        </FieldWrapper>
      </FormFieldset>
      <FormFieldset legend="SEO settings">
        <PathField
          register={register}
          error={errors.path}
          options={{ onBlur: addSlash, onChange: pathFormat }}
          name="path"
          label="Page path"
          placeholder={
            watch('name') ? '/' + slugify(watch('name')) + '/' : '/your-page/'
          }
          pathPrefix={
            siteData.type === 'template'
              ? '/location-path'
              : siteData.adjusted_base_directory != '/'
                ? siteData.adjusted_base_directory.slice(0, -1)
                : ''
          }
          autoComplete="off"
        />
        <TextField
          register={register}
          error={errors.title}
          name={`title`}
          label="Page title tag"
          helpText="Set a specific title that will be used within the page title tag"
        />
        <TextAreaField
          register={register}
          error={errors.meta_description}
          options={{ onChange: removeNewlines }}
          name="meta_description"
          label="Meta description"
          rows={3}
          helpText="Page meta description for the page."
        />
        <TextAreaField
          register={register}
          error={errors.meta_keywords}
          options={{ onChange: removeNewlines }}
          name="meta_keywords"
          label="Meta keywords"
          rows={2}
          helpText="Page meta keywords for the page"
        />
        <ToggleField
          register={register}
          control={control}
          error={errors.noindex}
          name="noindex"
          label="noindex"
          helpText="Set Page to Robots noindex for search engines."
        />
        <ToggleField
          register={register}
          control={control}
          error={errors.nofollow}
          name="nofollow"
          label="nofollow"
          helpText="Set Page to Robots nofollow for search engines."
        />
        <TextField
          register={register}
          error={errors.canonical}
          options={{ onChange: urlFormat }}
          name="canonical"
          label="Canonical URL"
          helpText="By default a canonical is set using the current page path. This field can be used to override it to point to different internal or external URL."
        />
        <TextField
          register={register}
          error={errors.custom_field_disable_canonical}
          name={`custom_field_disable_canonical`}
          label="Disable canonical from custom field"
          options={{
            onChange: slugifyUnderscore,
            onBlur: (e) => slugifyOnBlur(e, '_'),
          }}
          helpText="Key reference to the Site custom field that disables the Page canonical value (i.e. some_field)"
        />
      </FormFieldset>
      <FormFieldset legend="Meta">
        <TextField
          register={register}
          error={errors.partner_page_id}
          name="partner_page_id"
          label="Partner Page ID"
          helpText="Internal unique reference ID for this page"
        />
        <TextField
          type="number"
          register={register}
          error={errors.order}
          name="order"
          label="Page order"
          helpText="Relative page order to other pages within the same site. Used for sorting lists of pages. Suggest that you use increments of 10 when entering order."
        />
      </FormFieldset>
      <FormPublishingFooter
        disabled={disabled}
        draftMutation={mutation}
        draftUrl={`${siteData.preview_url.substring(0, siteData.preview_url.length - 1)}${pageData.path}?cache=none`}
        isDirty={isDirty}
        isDraft={pageData.is_draft}
        isError={isError}
        isValid={isValid}
        onRevert={onRevert}
        publishedUrl={`${siteData.formatted_base_url}${pageData.full_path}?cache=none`}
        publishMutation={publishMutation}
        showRevert={true}
      />
    </form>
  );
};
